import React, { useEffect, useMemo, useRef, useState } from "react";
import { weekDays, groupByDay, validateWeek } from "../../utils/weekUtils";
import { useAutosave } from "../../hooks/useAutosave";
import { useColumnWidths } from "../../hooks/useColumnWidths";
import {
  getTimeEntries, createTimeEntry, updateTimeEntry, deleteTimeEntry,
  submitWeek, submitDay, approveBulk, rejectEntry, getTasks, getOtherTasks,
} from "../../api";
import { asList, openOtherTasks, parsePickerValue } from "./taskOptions";
import DayGroup from "./DayGroup";
import NewTaskModal from "../NewTaskModal";
import StatusChip from "../StatusChip";
import { TE_STYLES } from "./teStyles";
import { onGridKeyDown } from "./gridNav";
import { deriveYearEnd } from "../../utils/yearEnd";

let LOCAL_SEQ = 1;

const COLUMNS = [
  { id: "code", label: "Code", w: 80 },
  { id: "client", label: "Client", w: 150 },
  { id: "task", label: "Task", w: 230 },
  { id: "year_end", label: "Year End", w: 80 },
  { id: "type", label: "Type", w: 96 },
  { id: "hrs", label: "Hrs", w: 60, num: true },
  { id: "notes", label: "Description", flex: true },
  { id: "status", label: "", w: 96 },
  { id: "del", label: "", w: 30, noResize: true },
];

const blankRow = (dayISO) => ({
  _localKey: `new_${LOCAL_SEQ++}`, date: dayISO, client: null, task: null, other_task: null,
  time_code: "", billable_code: "BILLABLE", hours: "", notes: "", year_end: null,
  status: "draft",
});

function padDays(saved, days, minRows) {
  const out = [];
  days.forEach((d) => {
    // The API hands these over newest-first ("-date", "-id" in
    // TimeEntryViewSet.get_queryset), which is right for the history lists
    // that share the endpoint and wrong for a timesheet: re-reading a day
    // would flip the lines the user had just typed into it. Ids are handed
    // out in creation order, so ascending id is entry order. Sorted here
    // rather than in the API so the history views keep their newest-first
    // reading; the live grid already appends new rows at the end, so this
    // only has to make a reload agree with what was on screen before it.
    const dayEntries = saved.filter((e) => e.date === d)
      .sort((a, b) => (a.id || 0) - (b.id || 0));
    out.push(...dayEntries);
    for (let i = dayEntries.length; i < minRows; i++) out.push(blankRow(d));
  });
  return out;
}

// createdById: whose week this is (the current user, or — in `review` mode — the
// employee the approver selected). In review mode the primary button approves the
// span and each saved line offers "Send back"; otherwise it's the employee's own grid.
// `days` overrides the Mon–Sun span — the Time Approval console passes a single
// day for its day-level drill-down.
export default function WeekView({ weekStart, createdById, clients, timeCodes, minRows = 5, review = false, days: daysProp, refreshToken = 0, showEmptyDays = true, taskTypes = [] }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [banner, setBanner] = useState(null);
  const [tasksByClient, setTasksByClient] = useState({});
  // The client's open Other tasks (PST filing, bookkeeping, ...), offered in
  // the same picker after its regular tasks. Kept apart from tasksByClient
  // because the two id sequences overlap -- see taskOptions.js.
  const [otherTasksByClient, setOtherTasksByClient] = useState({});
  // { entry, query } while the New Task form is open over the grid.
  const [newTask, setNewTask] = useState(null);
  // Widths are shared and remembered browser-wide, so Month mode's stack of
  // weekly grids stays aligned and a drag survives a reload.
  const [colW, setColW] = useColumnWidths(
    useMemo(() => Object.fromEntries(COLUMNS.filter((c) => !c.flex)
      .map((c) => [c.id, review && c.id === "del" ? 120 : c.w])), [review]));
  const { save, flush, statusByKey, errorByKey } = useAutosave(800);
  const days = useMemo(() => daysProp || weekDays(weekStart), [weekStart, daysProp]);
  const createPromises = useRef({});

  const startResize = (id, e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = colW[id];
    document.body.style.userSelect = "none";
    const move = (ev) => setColW(id, Math.max(44, startW + (ev.clientX - startX)));
    const up = () => {
      document.body.style.userSelect = "";
      document.removeEventListener("mousemove", move);
      document.removeEventListener("mouseup", up);
    };
    document.addEventListener("mousemove", move);
    document.addEventListener("mouseup", up);
  };

  const ensureTasksFor = async (clientId) => {
    if (!clientId || tasksByClient[clientId]) return;
    // Fetched on its own, so a failure here costs only the Other tasks and
    // never the regular list. horizon=all for the same reason as below; the
    // completed ones are dropped here (the endpoint has no such filter), since
    // a completed Other task refuses time just as a completed task does.
    (async () => {
      let others = [];
      try {
        others = openOtherTasks(asList(await getOtherTasks({ client: clientId, horizon: "all" })));
      } catch { /* the regular tasks still stand */ }
      setOtherTasksByClient((p) => ({ ...p, [clientId]: others }));
    })();
    try {
      // The picker answers "what may I log time against", not "what is on my
      // plate today" -- and logging the first hour is one of the ways a task
      // graduates from planned to active. horizon=all so forward-planned
      // work is pickable, not just what the server treats as active.
      // year_open: a closed year refuses time, so offering its tasks here
      // would hand the user a choice that can only end in a refusal.
      // bookable: open tasks, plus completed ones rolled forward but still
      // inside their grace period -- late time goes on the rolled file.
      const t = await getTasks({
        client: clientId, bookable: "true", horizon: "all", year_open: "true",
      });
      setTasksByClient((p) => ({ ...p, [clientId]: Array.isArray(t) ? t : (t.results || []) }));
    } catch {
      setTasksByClient((p) => ({ ...p, [clientId]: [] }));
    }
  };

  const load = async () => {
    setLoading(true);
    const data = await getTimeEntries({
      created_by: createdById, start_date: days[0], end_date: days[days.length - 1],
    });
    setEntries(padDays(data, days, minRows));
    setLoading(false);
    [...new Set(data.map((e) => e.client).filter(Boolean))].forEach(ensureTasksFor);
  };
  // refreshToken lets the page force a re-read -- the timer books its line
  // through the API directly, so the grid has to be told to go and look again.
  useEffect(() => { if (createdById) load(); /* eslint-disable-next-line */ },
    [weekStart, createdById, minRows, days[0], days[days.length - 1], refreshToken]);

  const sameRow = (x, entry) =>
    (x.id && x.id === entry.id) || (x._localKey && x._localKey === entry._localKey);

  // An untouched, unsaved blank line (ignores billable_code/date defaults).
  const isEmptyRow = (e) =>
    !e.id && !e.client && !e.time_code && !e.task && !e.other_task && !e.notes &&
    (e.hours === "" || e.hours == null) && (e.year_end == null || e.year_end === "");

  const persisted = entries.filter((e) => e.id);
  const hasSubmitted = persisted.some((e) => e.status === "submitted");
  const weekStatus = persisted.length
    ? (persisted.every((e) => e.status === "approved") ? "approved"
      : persisted.some((e) => e.status === "submitted") ? "submitted"
      : persisted.some((e) => e.status === "rejected") ? "rejected" : "draft")
    : "draft";

  const mergeWorkflow = (id, updated) =>
    setEntries((prev) => prev.map((x) => (x.id === id
      ? { ...x, status: updated.status, submitted_at: updated.submitted_at,
          approved_at: updated.approved_at, review_note: updated.review_note }
      : x)));

  const persist = (entry) => (payload) => {
    if (entry.id) {
      return updateTimeEntry(entry.id, payload).then((updated) => {
        mergeWorkflow(entry.id, updated); // reflect un-publish / re-queue live
        return updated;
      });
    }
    const lk = entry._localKey;
    if (createPromises.current[lk]) {
      // If the queued create already failed (e.g. a closed-year refusal),
      // `.then(onFulfilled)` alone would just forward that stale rejection
      // forever -- the row would never get a server id, the banner would
      // keep naming a client the user has since changed away from, and the
      // "fixed" edit would silently vanish on reload. Drop the dead promise
      // and retry as a fresh create with this edit's corrected payload; a
      // still-pending create keeps chaining onto it as before.
      //
      // But only retry when the rejection carries a real HTTP response
      // (api.js's handle() attaches `.response` only when one came back).
      // A transport failure -- a 502/504, or a dropped connection after the
      // server already committed the row -- rejects with no response at
      // all, and the row may already exist server-side. Retrying blind in
      // that case would fire a second createTimeEntry and double-book a
      // billable entry, so those keep forwarding the rejection instead.
      createPromises.current[lk] = createPromises.current[lk].then(
        (created) => updateTimeEntry(created.id, payload).then(() => created),
        (e) => {
          if (!e?.response?.data) return Promise.reject(e);
          delete createPromises.current[lk];
          return persist(entry)(payload);
        }
      );
      return createPromises.current[lk];
    }
    // Stamp the server id onto the row; do NOT replace the row with the
    // response. The user keeps typing during the ~800ms this takes, and the
    // response describes the row as it was when the create was queued -- so
    // painting it back wipes the client they picked in the meantime. Their
    // later edits are already on their way up via the createPromises chain.
    // `_localKey` is kept for the same reason it is kept everywhere: it is
    // this row's identity for as long as the grid is on screen.
    const p = createTimeEntry({ ...payload, date: entry.date }).then((created) => {
      setEntries((prev) => prev.map((x) => (x._localKey === lk
        ? { ...x, id: created.id, status: created.status } : x)));
      return created;
    });
    createPromises.current[lk] = p;
    return p;
  };

  // One patch of one or more fields onto one row: set the row, queue the save.
  // Callers that change a single cell go through onChange below; creating a
  // task sets the task AND its year in one go, which has to be one patch or
  // the two would race each other through the autosave queue.
  const applyChange = (entry, patch) => {
    const next = { ...entry, ...patch };
    if ("client" in patch) {
      next.task = null;
      next.other_task = null;
      if (patch.client) ensureTasksFor(patch.client);
    }

    // A task IS a fiscal year's worth of work, so picking one settles which
    // year the hour belongs to -- and settles it better than the date can.
    // Deriving from the date files every hour spent AFTER a year end into the
    // next year: a June-30 corporate's FY2026 file worked on in September
    // 2026 derived 2027, and January time on a personal FY2025 return derived
    // 2026. Overwrites whatever is in the cell, the same way picking a client
    // does; typing over it afterwards still wins, so the last deliberate act
    // is what sticks.
    //
    // Two deliberate non-actions. A task with no fiscal_year leaves the cell
    // alone -- nothing about it says which year it belongs to, and a guess
    // would be worse than the value already there. CLEARING the task leaves
    // it alone too: the year was right when it was stamped, and un-stamping
    // it would silently drop the entry into the unfiled bucket of the
    // client roll-up.
    if ("task" in patch && patch.task && !("year_end" in patch)) {
      const picked = (tasksByClient[next.client] || [])
        .find((t) => String(t.id) === String(patch.task));
      if (picked?.fiscal_year) next.year_end = picked.fiscal_year;
    }

    // Fill the year end from the client so bookkeeping time doesn't have to
    // have one typed. Fires on the two edits that change the right answer --
    // picking a client, and moving the entry to another date -- and on nothing
    // else, so a hand-typed override survives every other edit.
    //
    // Still editable: this is a default, not a lock. The client roll-up groups
    // by year_end (utils/rollup.js:113), so filling it is what puts the time in
    // a year row instead of the "unfiled" bucket.
    if (("client" in patch || "date" in patch) && !("year_end" in patch)) {
      const cl = (clients || []).find((c) => c.id === Number(next.client));
      const derived = deriveYearEnd(cl, next.date);
      if (derived) next.year_end = derived;
    }

    setEntries((prev) => {
      const updated = prev.map((x) => (sameRow(x, entry) ? next : x));
      // Auto-grow: once a day has no spare blank line left, append one.
      if (!review && !updated.some((x) => x.date === next.date && isEmptyRow(x))) {
        return [...updated, blankRow(next.date)];
      }
      return updated;
    });
    // Keyed by _localKey first so a row's autosave key does not change the
    // moment it gets an id -- see the note on the React key in DayGroup.
    const key = entry._localKey ?? entry.id;
    const payload = {
      date: next.date, client: next.client || null, task: next.task || null,
      other_task: next.other_task || null,
      time_code: next.time_code || "", billable_code: next.billable_code || "BILLABLE",
      hours: next.hours === "" ? "0" : next.hours, notes: next.notes || "",
      year_end: next.year_end ? Number(next.year_end) : null,
    };
    save(key, payload, persist(next));
  };

  // The Task picker hands back "t:<id>" or "o:<id>" (taskOptions.js), which
  // becomes BOTH fields in one patch: picking one kind clears the other.
  const onChange = (entry, field, value) => applyChange(entry,
    field === "task" ? parsePickerValue(value) : { [field]: value });

  // A task created from the grid is selected on the line that asked for it,
  // with its fiscal year stamped in the same patch: the picker's own lookup
  // reads tasksByClient, which has not been re-rendered yet at this point.
  const onTaskCreated = (entry, created) => {
    const cid = entry.client;
    setTasksByClient((p) => ({ ...p, [cid]: [...(p[cid] || []), created] }));
    setNewTask(null);
    applyChange(entry, {
      task: created.id,
      year_end: created.fiscal_year || entry.year_end || null,
    });
  };

  const onAddRow = (dayISO) => setEntries((prev) => [...prev, blankRow(dayISO)]);

  const onDelete = async (entry) => {
    setEntries((prev) => prev.filter((x) => !sameRow(x, entry)));
    if (entry.id) await deleteTimeEntry(entry.id);
  };

  const onSendBack = async (entry) => {
    const note = window.prompt("Reason to send this line back to the employee:");
    if (note === null) return;          // cancelled
    if (!note.trim()) {                  // a reason is mandatory — the server rejects blanks too
      setBanner({ type: "error", msg: "A reason is required to send time back." });
      return;
    }
    await rejectEntry(entry.id, note.trim());
    load();
  };

  const onSubmit = async () => {
    await flush();
    const saved = await getTimeEntries({
      created_by: createdById, start_date: days[0], end_date: days[days.length - 1],
    });
    const incomplete = validateWeek(saved);
    if (incomplete.length) {
      setBanner({ type: "error", msg: `${incomplete.length} line(s) are incomplete — fill in client, code, and hours.` });
      setEntries(padDays(saved, days, minRows));
      return;
    }
    try {
      const res = await submitWeek(weekStart, createdById);
      // Someone whose own time auto-approves gets it approved on the spot.
      setBanner({ type: "ok", msg: res?.approved != null
        ? "Submitted and approved." : "Submitted — awaiting approval." });
      load();
    } catch (e) {
      setBanner({ type: "error", msg: "Submit failed. Please try again." });
    }
  };

  // Submit just one day. Same checks as the week: anything incomplete on
  // that day blocks it, and the server re-checks.
  const onSubmitDay = async (dayISO) => {
    await flush();
    const saved = await getTimeEntries({
      created_by: createdById, start_date: dayISO, end_date: dayISO,
    });
    const incomplete = validateWeek(saved);
    if (incomplete.length) {
      setBanner({ type: "error", msg: `${incomplete.length} line(s) on that day are incomplete — fill in client, code, and hours.` });
      return;
    }
    try {
      const res = await submitDay(dayISO, createdById);
      setBanner({ type: "ok", msg: res?.approved != null
        ? "Day submitted and approved." : "Day submitted — awaiting approval." });
      load();
    } catch (e) {
      setBanner({ type: "error", msg: "Submit failed. Please try again." });
    }
  };

  const onApprove = async () => {
    await flush(); // land any inline edits (which re-queue to submitted) first
    try {
      await approveBulk(days[0], days[days.length - 1], [createdById]);
      setBanner({ type: "ok", msg: days.length === 1 ? "Day approved." : "Week approved." });
      load();
    } catch (e) {
      setBanner({ type: "error", msg: "Approve failed. Please try again." });
    }
  };

  const grouped = groupByDay(entries, days);
  const weekHours = entries.reduce((s, e) => s + (Number(e.hours) || 0), 0);
  const billableHours = entries.reduce((s, e) =>
    s + (e.billable_code === "NON_BILLABLE" ? 0 : Number(e.hours) || 0), 0);
  const nonBillableHours = entries.reduce((s, e) =>
    s + (e.billable_code === "NON_BILLABLE" ? Number(e.hours) || 0 : 0), 0);

  return (
    <div className="te-wrap">
      <style>{TE_STYLES}</style>
      <div className="te-bar">
        <div className="te-bar-left te-tiles">
          <div className="te-tile">
            <div className="te-tile-label">Total</div>
            <div className="te-tile-val">{weekHours.toFixed(2)}<span>hrs</span></div>
          </div>
          <div className="te-tile bill">
            <div className="te-tile-label">Billable</div>
            <div className="te-tile-val">{billableHours.toFixed(2)}<span>hrs</span></div>
          </div>
          <div className="te-tile nonbill">
            <div className="te-tile-label">Non-billable</div>
            <div className="te-tile-val">{nonBillableHours.toFixed(2)}<span>hrs</span></div>
          </div>
          {persisted.length > 0 && <StatusChip status={weekStatus} />}
        </div>
        {review ? (
          <button className="te-approve-wk" onClick={onApprove} disabled={!hasSubmitted}>
            {days.length === 1 ? "Approve day" : "Approve week"}
          </button>
        ) : (
          <button className="te-submit" onClick={onSubmit} disabled={weekStatus === "approved"}>
            Submit week for review
          </button>
        )}
      </div>

      {/* A refusal the server explained -- a closed year, a completed task.
          Shown above the grid rather than in the row, because the row's own
          status cell has only enough space for "retry". */}
      {Object.values(errorByKey).filter(Boolean).length > 0 && (
        <div className="te-banner te-banner-err">
          {Object.values(errorByKey).filter(Boolean)[0]}
        </div>
      )}

      {banner && (
        <div className={`te-banner ${banner.type === "ok" ? "te-banner-ok" : "te-banner-err"}`}>
          {banner.msg}
        </div>
      )}

      {loading ? (
        <div className="te-loading">Loading week…</div>
      ) : (
        <div className="te-frame">
          <table className="te-table" onKeyDown={onGridKeyDown}>
            <colgroup>
              {COLUMNS.map((c) => (
                <col key={c.id} style={c.flex ? undefined : { width: colW[c.id] }} />
              ))}
            </colgroup>
            <thead>
              <tr>
                {COLUMNS.map((c) => (
                  <th key={c.id} className={c.num ? "te-num" : undefined}>
                    {c.label}
                    {!c.flex && !c.noResize && (
                      <span className="te-resize" onMouseDown={(e) => startResize(c.id, e)} />
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            {/* A day with no time is still part of the week. Hiding it left an
                approver unable to tell "nothing was booked on Tuesday" from
                "Tuesday failed to load". The employee's own grid is unaffected
                -- it pads every day with blank rows already. */}
            {days.map((d) => (review && !showEmptyDays && grouped[d].length === 0 ? null : (
              <DayGroup key={d} dayISO={d} entries={grouped[d]} clients={clients}
                timeCodes={timeCodes} tasksByClient={tasksByClient}
                otherTasksByClient={otherTasksByClient} statusByKey={statusByKey}
                review={review} onChange={onChange} onDelete={onDelete}
                onSendBack={onSendBack} onAddRow={onAddRow}
                onSubmitDay={review ? undefined : onSubmitDay}
                onCreateTask={review ? undefined : (e, query) => setNewTask({ entry: e, query })} />
            )))}
          </table>
          {review && persisted.length === 0 && (
            <div className="te-loading">No time entered for this week.</div>
          )}
        </div>
      )}

      {newTask && (() => {
        const cl = (clients || []).find((c) => c.id === Number(newTask.entry.client));
        return (
          <NewTaskModal
            clientId={newTask.entry.client}
            clientName={cl?.name}
            isCorporate={cl?.is_corporate}
            fiscalMonthEnd={cl?.fiscal_month_end}
            taskTypeOptions={taskTypes}
            defaultName={newTask.query}
            // Never opens blank, and never on "this calendar year": the line's
            // own year if it has one, else the year that line's client and
            // date imply -- the same value the grid would have filled in.
            // The point of this form is not to ask for a fiscal year.
            defaultFiscalYear={newTask.entry.year_end
              || deriveYearEnd(cl, newTask.entry.date)}
            onClose={() => setNewTask(null)}
            onCreated={(created) => onTaskCreated(newTask.entry, created)}
          />
        );
      })()}
    </div>
  );
}
